import React, { useState } from "react";
import { injectIntl } from "react-intl";
import produce from "immer";
import isEqual from "lodash/isEqual";
import {
  Wrapper,
  Sections,
  Section,
  SectionLabel,
  SectionContent,
  SectionWrapper,
  InputGroup,
  Label,
  Input,
  Select,
  Checkbox,
  Buttons,
  Button,
} from "./styled";
const LabelValueSelector = React.lazy(() =>
  import("../../../../../../admin/src/components/ui/LabelValueSelector")
);

const Cell = ({
  data,
  rowIdx,
  cellIdx,
  setRows,
  setSettingsDraft,
  validations,
  intl,
}) => {
  const [values, setValues] = useState(data);

  const onChange = (key, value) => {
    setValues({ ...values, [key]: value });
  };

  const save = () => {
    setRows(
      produce((draft) => {
        draft[rowIdx].cells[cellIdx] = { ...data, ...values };
      })
    );

    setSettingsDraft(null);
  };

  const {
    type = "number",
    format,
    source = {},
    round = "",
    min,
    max,
    tdAlign = "center",
    prefix = "",
    suffix = "",
    roundedValueKey = "",
    disabled = false,
    hidden = false,
    validation = {},
    saveToKey,
  } = values;

  return (
    <Wrapper>
      <Sections>
        <SectionWrapper>
          <Section>
            <SectionLabel>
              {intl.formatMessage({ id: "Cell.Settings" })}
            </SectionLabel>
            <SectionContent>
              <InputGroup>
                <Label>
                  {intl.formatMessage({ id: "Cell.Settings.type" })}
                </Label>
                <Select
                  value={type}
                  onChange={(e) => onChange("type", e.target.value)}
                >
                  <option value="text">Text</option>
                  <option value="number">
                    {intl.formatMessage({ id: "Number" })}
                  </option>
                  <option value="date">
                    {intl.formatMessage({ id: "Date" })}
                  </option>
                  <option value="time">
                    {intl.formatMessage({ id: "Time" })}
                  </option>
                  <option value="datetime-local">
                    {intl.formatMessage({ id: "Date and Time" })}
                  </option>
                  <option value="dropdown">Dropdown</option>
                </Select>
              </InputGroup>
              {type === "number" && (
                <>
                  <InputGroup>
                    <Label>Format</Label>
                    <Select
                      value={format}
                      onChange={(e) => onChange("format", e.target.value)}
                    >
                      <option value="float">Float</option>
                      <option value="decimal">Decimal</option>
                      <option value="integer">Integer</option>
                    </Select>
                  </InputGroup>
                  {type === "number" && format === "integer" && (
                    <>
                      <InputGroup>
                        <Label>
                          {intl.formatMessage({ id: "Cell.Settings.round" })}
                        </Label>
                        <Select
                          value={round}
                          onChange={(e) => onChange("round", e.target.value)}
                        >
                          <option value="">
                            {intl.formatMessage({
                              id: "Cell.Settings.roundDefault",
                            })}
                          </option>
                          <option value="up">
                            {intl.formatMessage({
                              id: "Cell.Settings.roundUp",
                            })}
                          </option>
                          <option value="down">
                            {intl.formatMessage({
                              id: "Cell.Settings.roundDown",
                            })}
                          </option>
                        </Select>
                      </InputGroup>
                      <InputGroup>
                        <Label>
                          {intl.formatMessage({
                            id: "Cell.Settings.saveRoundAs",
                          })}
                        </Label>
                        <Input
                          value={roundedValueKey}
                          onChange={(e) =>
                            onChange("roundedValueKey", e.target.value)
                          }
                        />
                      </InputGroup>
                    </>
                  )}
                  <InputGroup>
                    <Label>
                      {intl.formatMessage({ id: "Cell.Settings.minValue" })}
                    </Label>
                    <Input
                      value={min}
                      onChange={(e) => onChange("min", e.target.value)}
                    />
                  </InputGroup>
                  <InputGroup>
                    <Label>
                      {intl.formatMessage({ id: "Cell.Settings.maxValue" })}
                    </Label>
                    <Input
                      value={max}
                      onChange={(e) => onChange("max", e.target.value)}
                    />
                  </InputGroup>
                </>
              )}
              {type === "dropdown" && (
                <InputGroup className="dropdown-settings">
                  <Label>
                    {intl.formatMessage({ id: "Cell.Settings.source" })}
                  </Label>
                  <LabelValueSelector
                    name={"options"}
                    data={values.source || source}
                    value={(values.source || source)["options"]}
                    save={(updateObj) => {
                      if (
                        values.source &&
                        isEqual(values.source[updateObj.key], updateObj.value)
                      )
                        return;

                      onChange("source", {
                        ...values.source,
                        [updateObj.key]: updateObj.value,
                      });
                    }}
                  />
                </InputGroup>
              )}
              <InputGroup>
                <Label>
                  {intl.formatMessage({ id: "Cell.Settings.alignment" })}
                </Label>
                <Select
                  value={tdAlign}
                  onChange={(e) => onChange("tdAlign", e.target.value)}
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </Select>
              </InputGroup>
              <InputGroup>
                <Label>Prefix</Label>
                <Input
                  value={prefix}
                  onChange={(e) => onChange("prefix", e.target.value)}
                />
              </InputGroup>
              <InputGroup>
                <Label>Suffix</Label>
                <Input
                  value={suffix}
                  onChange={(e) => onChange("suffix", e.target.value)}
                />
              </InputGroup>
              <InputGroup>
                <Checkbox
                  id="disabled"
                  checked={disabled}
                  onChange={() => onChange("disabled", !disabled)}
                />
                <Label htmlFor="disabled" float>
                  {intl.formatMessage({ id: "Cell.Settings.disabled" })}
                </Label>
              </InputGroup>
              <InputGroup>
                <Checkbox
                  id="hidden"
                  checked={hidden}
                  onChange={() => onChange("hidden", !hidden)}
                />
                <Label htmlFor="hidden" float>
                  {intl.formatMessage({ id: "Cell.Settings.hidden" })}
                </Label>
              </InputGroup>
              <InputGroup>
                <Label htmlFor="validation" float>
                  {intl.formatMessage({ id: "Cell.Settings.validation" })}
                </Label>
                <Select
                  value={validation._id}
                  onChange={(e) => {
                    onChange(
                      "validation",
                      validations.find((v) => v._id === e.target.value)
                    );
                  }}
                >
                  <option value="">
                    {intl.formatMessage({
                      id: "Cell.Settings.noValidation",
                    })}
                  </option>
                  {validations.map((validation) => (
                    <option value={validation._id} key={validation._id}>
                      {validation.name}
                    </option>
                  ))}
                </Select>
              </InputGroup>
              {/* If adding more options make sure to keep save to key option at the bottom of the modal */}
              <InputGroup>
                <Label>Save to key</Label>
                <Input
                  value={saveToKey}
                  onChange={(e) => onChange("saveToKey", e.target.value)}
                />
              </InputGroup>
            </SectionContent>
          </Section>
        </SectionWrapper>
      </Sections>
      <Buttons>
        <Button onClick={() => setSettingsDraft(null)}>
          {intl.formatMessage({ id: "Cell.Settings.cancel" })}
        </Button>
        <Button onClick={save}>
          {intl.formatMessage({ id: "Cell.Settings.save" })}
        </Button>
      </Buttons>
    </Wrapper>
  );
};

export default injectIntl(Cell);
